
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, Search, Trash2, Filter } from 'lucide-react';
import { getAllCourseAssignments, bulkUnassignCoursesFromUsers } from '@/hooks/auth/staffUserManagement/bulkOperationsService';

interface CourseAssignment {
  id: string;
  user_id: string;
  course_id: string;
  status: string;
  final_grade: number;
  assigned_date: string;
  profiles?: any;
}

export const BulkCourseUnassignManager: React.FC = () => {
  const [assignments, setAssignments] = useState<CourseAssignment[]>([]);
  const [filteredAssignments, setFilteredAssignments] = useState<CourseAssignment[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState(false);

  useEffect(() => {
    loadAssignments();
  }, []);

  useEffect(() => {
    filterAssignments();
  }, [searchQuery, statusFilter, assignments]);

  const loadAssignments = async () => {
    setLoading(true);
    try {
      const data = await getAllCourseAssignments();
      setAssignments(data);
    } finally {
      setLoading(false);
    }
  };

  const filterAssignments = () => {
    let filtered = [...assignments];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a =>
        a.profiles?.name?.toLowerCase().includes(query) ||
        a.profiles?.email?.toLowerCase().includes(query) ||
        a.course_id.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(a => a.status === statusFilter);
    }

    setFilteredAssignments(filtered);
  };

  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredAssignments.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredAssignments.map(a => a.id)));
    }
  };

  const handleBulkUnassign = async () => {
    setIsUnassigning(true);
    try {
      const result = await bulkUnassignCoursesFromUsers(Array.from(selectedIds));
      
      if (result.success) {
        setSelectedIds(new Set());
        await loadAssignments();
      }
    } finally {
      setIsUnassigning(false);
      setShowConfirmDialog(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Bulk Course Unassignment</CardTitle>
          <CardDescription>
            Select course assignments to remove from students
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters and Actions */}
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex flex-1 gap-2 w-full md:w-auto">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or course..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border rounded-md bg-background"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <Button
              variant="destructive"
              onClick={() => setShowConfirmDialog(true)}
              disabled={selectedIds.size === 0}
              className="w-full md:w-auto"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Unassign {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
            </Button>
          </div>

          {/* Results count */}
          <div className="text-sm text-muted-foreground">
            Showing {filteredAssignments.length} of {assignments.length} assignments
          </div>

          {/* Assignments Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedIds.size === filteredAssignments.length && filteredAssignments.length > 0}
                      onCheckedChange={toggleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Student</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Course</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Assigned</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAssignments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No course assignments found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAssignments.map((assignment) => (
                    <TableRow key={assignment.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(assignment.id)}
                          onCheckedChange={() => toggleSelection(assignment.id)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {assignment.profiles?.name || 'Unknown'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {assignment.profiles?.email}
                      </TableCell>
                      <TableCell>{assignment.course_id}</TableCell>
                      <TableCell>
                        <Badge variant={
                          assignment.status === 'completed' ? 'default' :
                          assignment.status === 'in_progress' ? 'secondary' :
                          'outline'
                        }>
                          {assignment.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{assignment.final_grade || 0}%</TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {new Date(assignment.assigned_date).toLocaleDateString()}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Bulk Unassignment</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to unassign <strong>{selectedIds.size}</strong> course{selectedIds.size > 1 ? 's' : ''} from students.
              This action cannot be undone. Students will lose access to these courses.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUnassigning}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkUnassign}
              disabled={isUnassigning}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isUnassigning && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirm Unassignment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
